/**
 * DIEUKYDIEU.TV - AUDIO PLAYER VOLUME, SLEEP TIMER & FAVORITES
 */

import { t } from '../i18n.js';
import { showToast } from '../ui.js';
import { audioPlaylist } from '../data/player-playlist.js';
import {
    currentAudioIndex,
    favoriteTracks,
    setFavoriteTracks
} from './player-state.js';
import { updatePlayState } from './player-dom.js';

let sleepTimerInterval = null;
let sleepTimerTargetTime = null;
let sleepOnEnd = false;

export function isSleepOnEnd() {
    return sleepOnEnd;
}

export function setVolume(val) {
    const audioEl = document.getElementById('main-audio');
    const slider = document.getElementById('fp-vol-slider');
    if (!audioEl) return;
    const safeVal = Math.max(0, Math.min(1, val));
    audioEl.volume = safeVal;
    audioEl.muted = false;
    if (slider) slider.value = safeVal;
    localStorage.setItem('dkd_player_volume', safeVal.toString());
    updateVolumeIcon();
}

export function initVolumeControl() {
    const slider = document.getElementById('fp-vol-slider');
    const audioEl = document.getElementById('main-audio');
    if (!audioEl) return;

    const savedVol = localStorage.getItem('dkd_player_volume');
    if (savedVol !== null) {
        audioEl.volume = parseFloat(savedVol);
    } else {
        audioEl.volume = 1;
    }
    if (slider) {
        slider.value = audioEl.volume;
        slider.addEventListener('input', (e) => {
            const val = parseFloat(e.target.value);
            audioEl.volume = val;
            audioEl.muted = false;
            localStorage.setItem('dkd_player_volume', val.toString());
            updateVolumeIcon();
        });
    }
    updateVolumeIcon();
}

export function updateVolumeIcon() {
    const icon = document.getElementById('fp-vol-icon');
    const iconMin = document.getElementById('fp-vol-icon-min');
    const audioEl = document.getElementById('main-audio');
    if (!audioEl) return;
    let iconName = 'volume_up';
    if (audioEl.muted || audioEl.volume === 0) {
        iconName = 'volume_off';
    } else if (audioEl.volume < 0.5) {
        iconName = 'volume_down';
    } else {
        iconName = 'volume_up';
    }
    if (icon) icon.innerText = iconName;
    if (iconMin) iconMin.innerText = (audioEl.muted || audioEl.volume === 0) ? 'volume_off' : 'volume_down';
}

export function toggleMute() {
    const audioEl = document.getElementById('main-audio');
    if (!audioEl) return;
    audioEl.muted = !audioEl.muted;
    const slider = document.getElementById('fp-vol-slider');
    if (slider) {
        slider.value = audioEl.muted ? 0 : audioEl.volume;
    }
    updateVolumeIcon();
    showToast(audioEl.muted ? t('toast_muted') : t('toast_unmuted'));
}

export function openSleepTimerModal() {
    const modal = document.getElementById('fp-sleep-modal');
    if (modal) modal.classList.add('show');
}

export function closeSleepTimerModal() {
    const modal = document.getElementById('fp-sleep-modal');
    if (modal) modal.classList.remove('show');
}

export function setSleepTimer(minutes) {
    if (sleepTimerInterval) {
        clearInterval(sleepTimerInterval);
        sleepTimerInterval = null;
    }

    const dot = document.getElementById('fp-timer-dot');
    const optSub = document.getElementById('fp-opt-timer-sub');
    const audioEl = document.getElementById('main-audio');

    if (minutes === 0) {
        sleepOnEnd = false;
        sleepTimerTargetTime = null;
        if (dot) dot.classList.remove('active');
        if (optSub) optSub.innerText = "Đang tắt";
        showToast(t('toast_timer_off'));
        closeSleepTimerModal();
        return;
    }

    if (minutes === 'end') {
        sleepOnEnd = true;
        sleepTimerTargetTime = null;
        if (dot) dot.classList.add('active');
        if (optSub) optSub.innerText = "Hết bài này";
        showToast(t('toast_timer_end_song'));
        closeSleepTimerModal();
        return;
    }

    sleepOnEnd = false;
    sleepTimerTargetTime = Date.now() + minutes * 60 * 1000;
    if (dot) dot.classList.add('active');
    if (optSub) optSub.innerText = `Sau ${minutes} phút`;
    showToast(`Hẹn giờ tắt nhạc sau ${minutes} phút`);
    closeSleepTimerModal();

    sleepTimerInterval = setInterval(() => {
        if (!sleepTimerTargetTime) return;
        const remainMs = sleepTimerTargetTime - Date.now();
        if (remainMs <= 0) {
            clearInterval(sleepTimerInterval);
            sleepTimerInterval = null;
            if (audioEl) audioEl.pause();
            updatePlayState(false);
            setSleepTimer(0);
            showToast(t('toast_timer_stopped'));
        } else {
            const remainMin = Math.ceil(remainMs / 60000);
            if (optSub) optSub.innerText = `Sau ${remainMin} phút`;
        }
    }, 1000);
}

export function toggleOptionsMenu(e) {
    if (e) e.stopPropagation();
    // Options menu eliminated in favor of clean Apple Music controls
}

export function closeOptionsMenu(e) {
    if (e) e.stopPropagation();
    const modal = document.getElementById('fp-options-modal');
    if (modal) modal.classList.remove('show');
}

export function openSleepTimerFromMenu() {
    // Sleep timer eliminated per user request
}

export function toggleFavoriteCurrentTrack(e) {
    if (e) e.stopPropagation();
    if (currentAudioIndex < 0 || currentAudioIndex >= audioPlaylist.length) return;
    const track = audioPlaylist[currentAudioIndex];
    const idx = favoriteTracks.indexOf(track.title);

    if (idx >= 0) {
        favoriteTracks.splice(idx, 1);
        updateFavoriteBtn(false);
        showToast(t('toast_fav_removed'));
    } else {
        favoriteTracks.push(track.title);
        updateFavoriteBtn(true);
        showToast(t('toast_fav_added'));
    }
    setFavoriteTracks(favoriteTracks);
    localStorage.setItem('dkd_player_favorites', JSON.stringify(favoriteTracks));
}

export function updateFavoriteBtn(isFav) {
    const btn = document.getElementById('fp-fav-btn');
    const icon = document.getElementById('fp-fav-icon');
    if (btn && icon) {
        btn.classList.toggle('favorited', isFav);
        icon.innerText = isFav ? 'favorite' : 'favorite_border';
    }
    const optBtn = document.getElementById('fp-opt-fav-btn');
    const optIcon = document.getElementById('fp-opt-fav-icon');
    if (optBtn && optIcon) {
        optBtn.classList.toggle('active', isFav);
        optIcon.innerText = isFav ? 'favorite' : 'favorite_border';
    }
}

export function shareCurrentTrack(e) {
    if (e) e.stopPropagation();
    const track = audioPlaylist[currentAudioIndex] || audioPlaylist[0];
    const shareText = `${track.title} - ${track.artist} | dieukydieu.tv`;

    if (navigator.share) {
        navigator.share({
            title: track.title,
            text: shareText,
            url: window.location.href
        }).catch(() => { });
    } else if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText).then(() => {
            showToast(t('toast_track_info_copied'));
        }).catch(() => showToast("Đã sao chép liên kết!"));
    }
}
